import { useCallback, useMemo, useRef } from 'react'
import { useFocusEffect } from '@react-navigation/native'
import { NotificationRecord } from '@src/models/notifications/types'
import { NotificationEngineNotificationActionKind } from '@src/types'
import { useDispatchAsync, useSnack } from '@src/utils'

interface UseAutoMarkNoneActionNotificationsReadParams {
  notifications: NotificationRecord[]
  enabled?: boolean
  delayMs?: number
}

export const useAutoMarkNoneActionNotificationsRead = ({
  notifications,
  enabled = true,
  delayMs = 450,
}: UseAutoMarkNoneActionNotificationsReadParams) => {
  const dispatchAsync = useDispatchAsync()
  const showSnack = useSnack()
  const autoMarkedNotificationIdsRef = useRef(new Set<string>())

  const autoReadableNotificationIds = useMemo(
    () =>
      notifications
        .filter(
          (notification) =>
            notification.actionKind === NotificationEngineNotificationActionKind.None &&
            !notification.readAt,
        )
        .map((notification) => notification.id),
    [notifications],
  )

  useFocusEffect(
    useCallback(() => {
      if (!enabled) {
        return
      }

      const unreadAutoReadableIds = new Set(autoReadableNotificationIds)

      autoMarkedNotificationIdsRef.current.forEach((id) => {
        if (!unreadAutoReadableIds.has(id)) {
          autoMarkedNotificationIdsRef.current.delete(id)
        }
      })

      const notificationIdsToMarkRead = autoReadableNotificationIds.filter(
        (id) => !autoMarkedNotificationIdsRef.current.has(id),
      )

      if (notificationIdsToMarkRead.length === 0) {
        return
      }

      notificationIdsToMarkRead.forEach((id) => {
        autoMarkedNotificationIdsRef.current.add(id)
      })

      let isActive = true

      const markNotificationsRead = async () => {
        try {
          await dispatchAsync({
            type: 'notifications/bulkMarkRead',
            payload: { notificationIds: notificationIdsToMarkRead },
          })
        } catch (error: any) {
          // Retry the batch on next focus cycle only if the mark call itself failed.
          notificationIdsToMarkRead.forEach((id) => {
            autoMarkedNotificationIdsRef.current.delete(id)
          })

          if (isActive) {
            showSnack(error?.message || 'Failed to mark as read.', null, 'error')
          }

          return
        }

        try {
          // Best-effort refresh of global badge source after local reducer updates.
          await dispatchAsync({
            type: 'notifications/fetchInboxSummary',
          })
        } catch (_error: any) {
          // Do not roll back successful mark-read mutations if summary refresh fails.
        }
      }

      // Preserve a short visual window where unread dots are still visible.
      // This allows the user to briefly see that there were some unread items before we clear them;
      // otherwise, it is not obvious that tapping into the inbox marked them as read as a side effect.
      setTimeout(markNotificationsRead, delayMs)

      return () => {
        isActive = false
      }
    }, [autoReadableNotificationIds, delayMs, dispatchAsync, enabled, showSnack]),
  )
}
